import { useState, useEffect, useRef, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import { UserInfo } from "@modules/shared/models/user.model";

interface UserSearchSelectProps {
  onSelect: (user: UserInfo | null) => void;
  placeholder?: string;
  clearable?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  searchFn: (keyword: string) => Promise<UserInfo[]>;
  selectedUser?: UserInfo | null;
  clearOnSelect?: boolean;
  minChars?: number;
}

export const UserSearchSelect = ({
  onSelect,
  placeholder = "Search by name or email...",
  clearable = true,
  disabled = false,
  autoFocus = false,
  searchFn,
  selectedUser = null,
  clearOnSelect = false,
  minChars = 2,
}: UserSearchSelectProps) => {
  const [keyword, setKeyword] = useState("");
  const [suggestions, setSuggestions] = useState<UserInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const search = useCallback(
    async (value: string) => {
      const trimmedValue = value.trim();
      if (trimmedValue.length < minChars) {
        if (isMounted.current) setSuggestions([]);
        return;
      }
      if (isMounted.current) setLoading(true);
      try {
        const results = await searchFn(trimmedValue);
        if (isMounted.current) setSuggestions(results);
      } catch (err) {
        console.error("Search failed", err);
        if (isMounted.current) setSuggestions([]);
      } finally {
        if (isMounted.current) setLoading(false);
      }
    },
    [searchFn, minChars],
  );

  useEffect(() => {
    if (!isOpen) return;
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      search(keyword);
    }, 300);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [keyword, isOpen, search]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (user: UserInfo) => {
    if (clearOnSelect) setKeyword("");
    else setKeyword(user.name || user.email);
    setSuggestions([]);
    setIsOpen(false);
    onSelect(user);
  };

  const handleClear = () => {
    setKeyword("");
    setSuggestions([]);
    onSelect(null);
    inputRef.current?.focus();
  };

  const handleFocus = () => {
    setIsOpen(true);
  };

  const handleImageError = (userId: string) => {
    setImageErrors((prev) => ({ ...prev, [userId]: true }));
  };

  const displayedValue = selectedUser
    ? selectedUser.name || selectedUser.email
    : keyword;
  const trimmedKeyword = keyword.trim();
  const hasMinChars = trimmedKeyword.length >= minChars;

  const showTypeHint = isOpen && !loading && !trimmedKeyword;
  const showLoadingHint = isOpen && loading;
  const showMinCharHint = isOpen && !loading && trimmedKeyword && !hasMinChars;
  const showNoResults =
    isOpen && !loading && hasMinChars && suggestions.length === 0;

  const menuStyle = {
    maxHeight: 250,
    overflowY: "auto" as const,
    position: "absolute" as const,
    zIndex: 2000,
    top: "100%",
    left: 0,
    right: 0,
    marginTop: 4,
  };

  return (
    <div ref={wrapperRef} className="position-relative">
      <div className="input-group">
        <input
          ref={inputRef}
          type="text"
          className="form-control"
          placeholder={placeholder}
          value={displayedValue}
          onChange={(e) => {
            setKeyword(e.target.value);
            setIsOpen(true);
            if (selectedUser) onSelect(null);
          }}
          onFocus={handleFocus}
          disabled={disabled}
          autoFocus={autoFocus}
        />
        {clearable && (selectedUser || keyword) && !disabled && (
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleClear}
          >
            ✕
          </button>
        )}
        {loading && (
          <span className="input-group-text">
            <Spinner animation="border" size="sm" />
          </span>
        )}
      </div>

      {isOpen && suggestions.length > 0 && (
        <ul className="dropdown-menu show w-100 shadow-sm" style={menuStyle}>
          {suggestions.map((user) => {
            const hasError = imageErrors[user.id];
            const displayName = user.name || user.email || "Unknown";
            return (
              <li
                key={user.id}
                className="dropdown-item"
                onClick={() => handleSelect(user)}
                style={{ cursor: "pointer" }}
              >
                <div className="d-flex align-items-center">
                  {user.avatar && !hasError ? (
                    <img
                      src={user.avatar}
                      alt=""
                      className="rounded-circle me-2"
                      style={{ width: 32, height: 32, objectFit: "cover" }}
                      onError={() => handleImageError(user.id)}
                    />
                  ) : (
                    <div
                      className="rounded-circle me-2 bg-light d-flex align-items-center justify-content-center"
                      style={{
                        width: 32,
                        height: 32,
                        fontSize: 12,
                        color: "#6c757d",
                      }}
                    >
                      {displayName[0]?.toUpperCase() || "?"}
                    </div>
                  )}
                  <div>
                    <span>{displayName}</span>
                    {user.email && user.email !== displayName && (
                      <div className="small text-muted">{user.email}</div>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {showTypeHint && (
        <div
          className="dropdown-menu show w-100 p-2 text-muted text-center"
          style={menuStyle}
        >
          Start typing to search
        </div>
      )}
      {showLoadingHint && (
        <div
          className="dropdown-menu show w-100 p-2 text-muted text-center"
          style={menuStyle}
        >
          Searching...
        </div>
      )}
      {showMinCharHint && (
        <div
          className="dropdown-menu show w-100 p-2 text-muted text-center"
          style={menuStyle}
        >
          Type at least {minChars} characters to search
        </div>
      )}
      {showNoResults && (
        <div
          className="dropdown-menu show w-100 p-2 text-muted text-center"
          style={menuStyle}
        >
          No users found
        </div>
      )}
    </div>
  );
};
